import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, message, Space, Tag, Avatar, Row, Col, Typography } from 'antd';
import { UserOutlined, ReloadOutlined, ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { apiNvTeamDiemDanh, apiGetAllUser } from '../services/authService';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

const DiemDanhComponent = () => {
    const [loading, setLoading] = useState(false);
    const [diemDanhData, setDiemDanhData] = useState([]);
    const [boPhan, setBoPhan] = useState('');
    const [ngayHienTai] = useState(dayjs());

    const renderMemberCard = (member, index) => {
        const gioVao = member.thoiGianVao ? dayjs(member.thoiGianVao) : null;
        const gioRa = member.thoiGianRa ? dayjs(member.thoiGianRa) : null;

        // Kiểm tra có điểm danh hay không
        const hasAttendance = member.image && member.thoiGianVao;
        const cardColor = hasAttendance ? '#f6ffed' : '#fff2f0'; // Xanh nhạt hoặc đỏ nhạt
        const borderColor = hasAttendance ? '#52c41a' : '#ff4d4f'; // Xanh hoặc đỏ

        const getStatusTag = () => {
            if (hasAttendance) {
                if (gioVao && gioRa) {
                    return <Tag color="green">Đã về</Tag>;
                } else {
                    return <Tag color="green">Đang làm</Tag>;
                }
            } else {
                return <Tag color="red">Vắng</Tag>;
            }
        };

        const getTotalHours = () => {
            if (gioVao && gioRa) {
                const diff = gioRa.diff(gioVao, 'hour', true);
                return `${diff.toFixed(1)}h`;
            }
            return hasAttendance ? 'Đang làm' : '-';
        };

        return (
            <Col
                xs={24}
                sm={12}
                md={8}
                lg={4}
                xl={4}
                xxl={4}
                key={member.id || index}
            >
                <Card
                    hoverable
                    style={{
                        borderRadius: '12px',
                        backgroundColor: cardColor,
                        border: `2px solid ${borderColor}`,
                        boxShadow: `0 4px 12px ${hasAttendance ? 'rgba(82, 196, 26, 0.2)' : 'rgba(255, 77, 79, 0.2)'}`,
                        marginBottom: '12px',
                        height: '320px', // Giảm chiều cao từ 380px xuống 320px
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    styles={{
                        body: {
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            padding: '12px' // Giảm padding từ 16px xuống 12px
                        }
                    }}
                >
                    {/* Phần header - Avatar, tên, bộ phận */}
                    <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                        <Avatar
                            size={48}
                            src={member.avatar}
                            icon={<UserOutlined />}
                            style={{ marginBottom: '6px' }}
                        />
                        <Title level={5} style={{ margin: '4px 0', color: '#1890ff', fontSize: '14px' }}>
                            {member.username}
                        </Title>
                        <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
                            <Tag color="blue" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                {member.part || member.department || 'Chưa xác định'}
                            </Tag>
                            <Tag color="geekblue" style={{ fontSize: '10px' }}>
                                ID: {member.id}
                            </Tag>
                        </div>
                    </div>

                    {/* Phần trạng thái */}
                    <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                        {getStatusTag()}
                    </div>

                    {/* Phần thông tin thời gian - tối ưu layout */}
                    <div style={{ minHeight: '80px' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {/* Giờ vào và giờ ra trên cùng 1 hàng */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                    <Text type="secondary" style={{ fontSize: '11px', marginRight: '4px' }}>
                                        <ClockCircleOutlined /> Vào:
                                    </Text>
                                    <Text strong style={{ fontSize: '12px' }}>
                                        {gioVao ? gioVao.format('HH:mm') : '-'}
                                    </Text>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                                    <Text type="secondary" style={{ fontSize: '11px', marginRight: '4px' }}>
                                        Ra:
                                    </Text>
                                    <Text strong style={{ fontSize: '12px' }}>
                                        {gioRa ? gioRa.format('HH:mm') : '-'}
                                    </Text>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text type="secondary" style={{ fontSize: '11px' }}>Tổng giờ:</Text>
                                <Text strong style={{ color: hasAttendance ? '#52c41a' : '#999', fontSize: '12px' }}>
                                    {getTotalHours()}
                                </Text>
                            </div>
                        </Space>
                    </div>

                    {/* Phần tọa độ - chỉ hiển thị khi có */}
                    {member.toa_do && (
                        <div style={{ marginTop: 'auto', paddingTop: '6px', borderTop: '1px solid #f0f0f0' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <EnvironmentOutlined /> {member.toa_do}
                            </Text>
                        </div>
                    )}
                </Card>
            </Col>
        );
    };

    // Function để nối dữ liệu user với điểm danh
    const mergeUserWithAttendance = (users, attendance) => {
        return users.map(user => {
            // Tìm thông tin điểm danh của user này
            const userAttendance = attendance.find(att => att.user_id === user.id);

            if (userAttendance) {
                // Nếu có điểm danh, nối thông tin
                return {
                    ...user,
                    ...userAttendance,
                    // Giữ avatar từ user, không dùng image từ attendance
                    avatar: user.avatar
                };
            } else {
                // Nếu không có điểm danh, chỉ trả về thông tin user
                return {
                    ...user,
                    thoiGianVao: null,
                    thoiGianRa: null,
                    image: null,
                    toa_do: null
                };
            }
        });
    };

    useEffect(() => {
        // Đặt bộ phận mặc định là tất cả 3 bộ phận
        setBoPhan('Tất cả');
    }, []);

    // Function để load tất cả dữ liệu từ 4 bộ phận
    const loadAllData = useCallback(async () => {
        setLoading(true);
        try {
            const departments = ['Máy tính', 'Công trình', 'Dự án', 'Photocopy'];

            // Load users và attendance từ cả 4 bộ phận song song
            const [usersResponse, ...attendanceResponses] = await Promise.all([
                apiGetAllUser(),
                ...departments.map(dept => apiNvTeamDiemDanh(dept, ngayHienTai.format('YYYY-MM-DD')))
            ]);

            console.log('=== LOAD ALL DATA ===');
            console.log('Users Response:', usersResponse);
            console.log('Attendance Responses:', attendanceResponses);

            // Xử lý dữ liệu users - API trả về response trực tiếp
            const allUsersData = Array.isArray(usersResponse) ? usersResponse : [];

            // Lọc users từ 4 bộ phận
            const allDepartmentUsers = allUsersData.filter(user =>
                departments.includes(user.part)
            );

            // Gộp tất cả dữ liệu điểm danh từ 4 bộ phận
            let allAttendanceData = [];
            attendanceResponses.forEach((response, index) => {
                const attendanceArray = response.data && Array.isArray(response.data) ? response.data : [];
                allAttendanceData = [...allAttendanceData, ...attendanceArray];
                console.log(`Điểm danh ${departments[index]}:`, attendanceArray.length);
            });

            console.log('=== PROCESSED DATA ===');
            console.log('Tổng users:', allUsersData.length);
            console.log('Users 4 bộ phận:', allDepartmentUsers.length);
            console.log('Tổng điểm danh hôm nay:', allAttendanceData.length);
            console.log('====================');

            // Nối dữ liệu và lưu trực tiếp
            const mergedData = mergeUserWithAttendance(allDepartmentUsers, allAttendanceData);
            setDiemDanhData(mergedData);

            message.success(`Hiển thị ${allDepartmentUsers.length} nhân viên từ 4 bộ phận (${allAttendanceData.length} có điểm danh)`);
        } catch (error) {
            console.log('=== LOAD ALL DATA ERROR ===');
            console.log('Error:', error);
            message.error('Không thể tải dữ liệu');
        } finally {
            setLoading(false);
        }
    }, [ngayHienTai]);

    useEffect(() => {
        // Auto load data từ tất cả bộ phận
        loadAllData();
    }, [loadAllData]);

    return (
        <div>
            <Card
                title="Danh sách thành viên điểm danh"
                style={{ marginBottom: '16px' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
                    <Space size="large">
                        <div>
                            <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Bộ phận:</span>
                            <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                Tất cả (Máy tính, Công trình, Dự án, Photocopy)
                            </Tag>
                        </div>

                        <div>
                            <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Ngày:</span>
                            <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                {ngayHienTai.format('DD/MM/YYYY')}
                            </Tag>
                        </div>
                    </Space>

                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={loadAllData}
                        loading={loading}
                    >
                        Tải lại
                    </Button>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Text>Đang tải dữ liệu...</Text>
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    Bộ phận: <Text strong style={{ color: '#1890ff' }}>Tất cả (Máy tính, Công trình, Dự án, Photocopy)</Text>
                                </Text>
                            </div>
                            <div>
                                <Text type="secondary">
                                    Tổng {diemDanhData.length} thành viên • {diemDanhData.filter(m => m.thoiGianVao).length} có điểm danh
                                </Text>
                            </div>
                        </div>
                        <Row gutter={[12, 12]}>
                            {diemDanhData.map((member, index) =>
                                renderMemberCard(member, index)
                            )}
                        </Row>
                        {diemDanhData.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <Text type="secondary">Không có dữ liệu điểm danh</Text>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
};

export default DiemDanhComponent;
